/**
 * MEXC Protobuf Decoder
 *
 * Provides compile-time typed decoding for MEXC WebSocket binary messages.
 * The proto schema is embedded as a string constant and compiled once at module load.
 *
 * @module cex-sdk/exchanges/mexc/proto/decoder
 */

import * as protobuf from 'protobufjs';

import type { PushDataV3ApiWrapper } from './types';

/**
 * Embedded proto schema - compiled once at module initialization.
 * This eliminates runtime fetch and provides instant decoding.
 */
const MEXC_PROTO_SCHEMA = `
syntax = "proto3";

message PushDataV3ApiWrapper {
  string channel = 1;
  oneof body {
    PublicSpotKlineV3Api publicSpotKline = 308;
    PublicMiniTickerV3Api publicMiniTicker = 309;
    PublicAggreDepthsV3Api publicAggreDepths = 313;
    PublicAggreDealsV3Api publicAggreDeals = 314;
    PublicAggreBookTickerV3Api publicAggreBookTicker = 315;
  }
  string symbol = 3;
  string symbolId = 4;
  int64 createTime = 5;
  int64 sendTime = 6;
}

message PublicAggreDealsV3Api {
  repeated PublicAggreDealsV3ApiItem dealsList = 1;
  string eventType = 2;
}

message PublicAggreDealsV3ApiItem {
  string price = 1;
  string quantity = 2;
  int32 tradeType = 3;
  int64 time = 4;
}

message PublicAggreDepthsV3Api {
  repeated PublicAggreDepthV3ApiItem asks = 1;
  repeated PublicAggreDepthV3ApiItem bids = 2;
  string eventType = 3;
  string fromVersion = 4;
  string toVersion = 5;
}

message PublicAggreDepthV3ApiItem {
  string price = 1;
  string quantity = 2;
}

message PublicSpotKlineV3Api {
  string interval = 1;
  int64 windowStart = 2;
  string openingPrice = 3;
  string closingPrice = 4;
  string highestPrice = 5;
  string lowestPrice = 6;
  string volume = 7;
  string amount = 8;
  int64 windowEnd = 9;
}

message PublicAggreBookTickerV3Api {
  string bidPrice = 1;
  string bidQuantity = 2;
  string askPrice = 3;
  string askQuantity = 4;
}

message PublicMiniTickerV3Api {
  string symbol = 1;
  string price = 2;
  string rate = 3;
  string zonedRate = 4;
  string high = 5;
  string low = 6;
  string volume = 7;
  string quantity = 8;
  string lastCloseRate = 9;
  string lastCloseZonedRate = 10;
  string lastCloseHigh = 11;
  string lastCloseLow = 12;
}
`;

/**
 * Compiled protobuf type - initialized once at module load
 */
let messageType: protobuf.Type | null = null;
let initError: Error | null = null;

// Compile the proto schema immediately at module load
try {
  const root = protobuf.parse(MEXC_PROTO_SCHEMA).root;
  messageType = root.lookupType('PushDataV3ApiWrapper');
} catch (error) {
  initError = error instanceof Error ? error : new Error(String(error));
  console.error('[MEXC Proto] Failed to compile protobuf schema:', initError);
}

/**
 * Decoding options for protobufjs
 */
const DECODE_OPTIONS: protobuf.IConversionOptions = {
  longs: String, // Convert int64 to string for precision
  enums: String,
  bytes: String,
  defaults: true,
  arrays: true,
  objects: true,
};

/**
 * Check if the decoder is ready to use
 */
export function isDecoderReady(): boolean {
  return messageType !== null;
}

/**
 * Get any initialization error that occurred
 */
export function getDecoderError(): Error | null {
  return initError;
}

/**
 * Decode a binary protobuf message to typed object
 *
 * @param buffer - Binary data from WebSocket message
 * @returns Decoded and typed message, or undefined if decoding fails
 *
 * @example
 * ```typescript
 * ws.onmessage = async (event) => {
 *   if (event.data instanceof Blob) {
 *     const buffer = await event.data.arrayBuffer();
 *     const message = decodeMexcMessage(new Uint8Array(buffer));
 *     if (message?.publicAggreDeals) {
 *       // Handle trades
 *     }
 *   }
 * };
 * ```
 */
export function decodeMexcMessage(buffer: Uint8Array): PushDataV3ApiWrapper | undefined {
  if (!messageType) {
    console.warn('[MEXC Proto] Decoder not initialized');
    return undefined;
  }

  try {
    const decoded = messageType.decode(buffer);
    const message = messageType.toObject(decoded, DECODE_OPTIONS) as PushDataV3ApiWrapper;
    return message;
  } catch (error) {
    console.error('[MEXC Proto] Failed to decode message:', error);
    return undefined;
  }
}

/**
 * Decode a Blob to typed message (convenience wrapper)
 *
 * @param blob - Blob data from WebSocket message
 * @returns Promise resolving to decoded message or undefined
 */
export async function decodeMexcBlob(blob: Blob): Promise<PushDataV3ApiWrapper | undefined> {
  try {
    const arrayBuffer = await blob.arrayBuffer();
    return decodeMexcMessage(new Uint8Array(arrayBuffer));
  } catch (error) {
    console.error('[MEXC Proto] Failed to read blob:', error);
    return undefined;
  }
}

/**
 * Decode ArrayBuffer to typed message (convenience wrapper)
 *
 * @param arrayBuffer - ArrayBuffer data from WebSocket message
 * @returns Decoded message or undefined
 */
export function decodeMexcArrayBuffer(arrayBuffer: ArrayBuffer): PushDataV3ApiWrapper | undefined {
  return decodeMexcMessage(new Uint8Array(arrayBuffer));
}
